import React, { useState } from 'react';
import { useTranslations } from '../translations';
import { ASSETS } from '../constants/assets';
import './ReportModal.css';

export interface ReportModalProps {
  onClose: () => void;
  onSubmit: (reason: string, otherText?: string) => void;
  kind?: 'product' | 'user' | 'chat';
}

const ReportModal: React.FC<ReportModalProps> = ({ onClose, onSubmit, kind = 'product' }) => {
  const t = useTranslations();
  const [selectedReason, setSelectedReason] = useState('spam');
  const [otherText, setOtherText] = useState('');
  const copy: Record<string, string> = {
    product: t.reportModal,
    user: t.reportUserModal,
    chat: t.reportChatModal,
  }[kind];
  const reasons = {
    product: ['spam', 'wrongCategory', 'forbiddenProduct', 'expiredProduct', 'fraud', 'other'],
    user: ['spam', 'inappropriateContent', 'harassment', 'scam', 'fakeProfile', 'sellerStatus', 'other'],
    chat: ['spam', 'harassment', 'inappropriateContent', 'scam', 'offensiveLanguage', 'other'],
  }[kind];

  const handleSubmit = () => {
    if (selectedReason === 'other' && !otherText.trim()) return;
    onSubmit(selectedReason, selectedReason === 'other' ? otherText : undefined);
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const renderRadioIcon = (checked: boolean) => (
    checked ? (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="11" fill="#F5FAF2" stroke="#267200" strokeWidth="2" />
        <path d="M16.8 8.7L10.9 15.3L7.2 11.8" stroke="#267200" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10.8" stroke="#596255" strokeWidth="2.4" />
      </svg>
    )
  );

  return (
    <div className="report-modal-overlay" onClick={handleOverlayClick}>
      <div className="report-modal-container" role="dialog" aria-modal="true" aria-labelledby="report-modal-title">
        <div className="report-modal-bg"></div>

        <button className="report-modal-close" onClick={onClose} aria-label="Close">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.3568 19.9999L29.5118 12.8449C30.1635 12.1933 30.1635 11.1399 29.5118 10.4883C28.8602 9.8366 27.8068 9.8366 27.1552 10.4883L20.0002 17.6433L12.8452 10.4883C12.1935 9.8366 11.1402 9.8366 10.4885 10.4883C9.83684 11.1399 9.83684 12.1933 10.4885 12.8449L17.6435 19.9999L10.4885 27.1549C9.83684 27.8066 9.83684 28.8599 10.4885 29.5116C10.8135 29.8366 11.2402 29.9999 11.6668 29.9999C12.0935 29.9999 12.5202 29.8366 12.8452 29.5116L20.0002 22.3566L27.1552 29.5116C27.4802 29.8366 27.9068 29.9999 28.3335 29.9999C28.7602 29.9999 29.1868 29.8366 29.5118 29.5116C30.1635 28.8599 30.1635 27.8066 29.5118 27.1549L22.3568 19.9999Z" fill="#071105"/>
          </svg>
        </button>

        <div className="report-modal-body">
          <img src={ASSETS.REPORT} alt="report" className="report-modal-icon" />
          <h2 id="report-modal-title" className="report-modal-title">{copy.title}</h2>
          <p className="report-modal-question">{copy.question}</p>

          <div className="report-modal-options">
            {reasons.map((reason) => (
              <label className="report-modal-option" key={reason}>
                <input
                  type="radio"
                  name={kind === 'chat' ? 'chat-report-reason' : 'reason'}
                  value={reason}
                  checked={selectedReason === reason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className="report-modal-radio-input"
                />
                <div className="report-modal-radio">
                  {renderRadioIcon(selectedReason === reason)}
                </div>
                <span className="report-modal-option-text">{copy[reason]}</span>
              </label>
            ))}
          </div>

          <textarea
            className="report-modal-other-input"
            placeholder={copy.otherPlaceholder}
            aria-label={copy.otherPlaceholder}
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            disabled={selectedReason !== 'other'}
          />
        </div>
        <div className="report-modal-actions">
          <button type="button" className="report-modal-cancel" onClick={onClose}>{t.common.cancel}</button>
          <button type="button" className="report-modal-submit" onClick={handleSubmit}>{copy.submit}</button>
        </div>
      </div>
    </div>
  );
};

export default ReportModal;
